import { Theme, useTheme } from '@mui/material';
import { ComponentProps, ReactElement, createContext, useContext, useMemo } from 'react';

import type { DialogLazyComponentsType, DialogType } from './DialogManager';
import type { ManagedAxleDialogProps } from './ManagedAxleDialog';

// Kept in a separate file so createContext() is never re-executed during HMR.
// All imports from ./DialogManager are type-only (erased at runtime), so this
// module has no runtime dep on DialogManager and won't be invalidated by HMR
// when dialog files change.

export interface IDialogOptions {
    closeOnNavigationChange?: boolean;
}

export interface IActiveDialog {
    id: string;
    type: DialogType;
    component: () => ReactElement;
    options: IDialogOptions;
    // The theme of the component that opened the dialog.
    theme?: Theme;
}

type DialogProps<T extends DialogType> = Omit<
    ComponentProps<DialogLazyComponentsType[T]>,
    keyof ManagedAxleDialogProps
>;

export interface IDialogActionsContext {
    openDialog: <T extends DialogType>(type: T, props: DialogProps<T>) => void;
}

// What DialogProvider supplies; useDialog fills in the theme.
export interface IDialogProviderActions {
    openDialog: <T extends DialogType>(type: T, props: DialogProps<T>, theme?: Theme) => void;
}

// The actions (openDialog) and the active-dialog state live in separate
// contexts on purpose. openDialog is a stable callback, while activeDialogs
// changes every time a dialog opens or closes. Keeping them apart means the
// many components that only call `useDialog()` to open dialogs (e.g. the
// schedule calendar) do not re-render when a dialog is opened — only the few
// components that actually read the active-dialog state do.
export const DialogActionsContext = createContext<IDialogProviderActions | null>(null);
export const DialogStateContext = createContext<IActiveDialog[] | null>(null);

// Dialogs render at the root, outside the page that opens them, so openDialog
// carries the caller's theme along for the dialog to render with.
export const useDialog = (): IDialogActionsContext => {
    const context = useContext(DialogActionsContext);
    const theme = useTheme();
    const actions = useMemo(
        () =>
            context && {
                openDialog: ((type, props) =>
                    context.openDialog(type, props, theme)) as IDialogActionsContext['openDialog'],
            },
        [context, theme],
    );
    if (!actions) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return actions;
};

export const useActiveDialogs = () => {
    const context = useContext(DialogStateContext);
    if (context === null) {
        throw new Error('useActiveDialogs must be used within a DialogProvider');
    }
    return context;
};
